import { inject, injectable } from 'inversify';
import { getFromQuery } from '../utilities/get-from-query.utility';
import { TYPES } from '../di/types';
import { TealiumAnalyticsService } from './TealiumAnalyticsService';
import {
  AnalyticsOptions,
  AnalyticsPayload,
  AnalyticsType,
  AnalyticsService,
  ClickstreamPayload,
  AnalyticsPageviewPayload,
  GoogleAnalyticsPayload,
  AnalyticsPageviewOptions,
  ClickstreamExperimentPayload,
} from './types';

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class WebAnalyticsService implements AnalyticsService {
  private currentPageName: string;
  private currentPageOptions: AnalyticsPageviewOptions;

  constructor(
    @inject(TYPES.TealiumAnalyticsService)
    private readonly tealiumAnalytics: TealiumAnalyticsService,
    @inject(TYPES.Window) private readonly windowInstance: Window,
    @inject(TYPES.AppConfig) private readonly appConfig: AppConfig,
  ) {
    this.currentPageName = '';
    this.currentPageOptions = {};
  }

  public logAnalytics(
    options: AnalyticsOptions,
    payload: AnalyticsPayload,
  ): void {
    if (options.isPageview) {
      this.logGaPageviewEvent(payload as AnalyticsPageviewPayload);
      this.logClickstreamPageviewEvent(payload as AnalyticsPageviewPayload);
    } else if (options.type === AnalyticsType.GOOGLE) {
      this.logGaEvent(payload as GoogleAnalyticsPayload);
    } else if (options.type === AnalyticsType.CLICKSTREAM_EXPERIMENT) {
      this.logClickstreamExperiment(payload as ClickstreamExperimentPayload);
    } else {
      this.logClickstreamEvent(payload as ClickstreamPayload);
    }
  }

  public refirePageview(): void {
    this.sendPageview(
      this.currentPageName,
      AnalyticsType.ALL,
      this.currentPageOptions,
    );
  }

  public logPageview(
    pageName: string,
    analyticsType: AnalyticsType = AnalyticsType.ALL,
    options: AnalyticsPageviewOptions = {},
  ): void {
    if (pageName.toLowerCase() !== this.currentPageName.toLowerCase()) {
      this.sendPageview(pageName, analyticsType, options);
      this.setCurrentPageInfo(pageName);
    }
  }

  private setCurrentPageInfo(
    pageName: string,
    options?: AnalyticsPageviewOptions,
  ): void {
    this.currentPageName = pageName;
    this.currentPageOptions = options;
  }

  private sendPageview(
    pageName: string,
    analyticsType: AnalyticsType = AnalyticsType.ALL,
    options: AnalyticsPageviewOptions = {},
  ): void {
    const {
      pageGroup = 'convenience features',
      subGroup = 'help',
      dataLayer = null,
      vars = {},
    } = options;
    const dataLayerPayload = dataLayer ? { dataLayer } : {};

    this.logAnalytics(
      {
        type: analyticsType,
        isPageview: true,
      },
      {
        screen_name: pageName,
        page_group: pageGroup,
        sub_group: subGroup,
        brand: this.appConfig.brand,
        clickstreamData: {
          pageName,
          vars,
        },
        ...dataLayerPayload,
      },
    );
  }

  private logGaPageviewEvent(payload: AnalyticsPageviewPayload): void {
    this.tealiumAnalytics.logPageViewEvent(payload);
  }

  private logClickstreamPageviewEvent(payload: AnalyticsPageviewPayload): void {
    const args: any[] = ['setView', payload.screen_name];
    const refPage = getFromQuery(
      'clickstreamRefPage',
      this.windowInstance.location.search,
    );
    const refCta = getFromQuery(
      'clickstreamRefCta',
      this.windowInstance.location.search,
    );

    const data = payload.clickstreamData ?? {};

    if (refPage || refCta) {
      data.vars = {
        ...(data.vars ?? {}),
        refPage,
        refCta,
      };
    }

    if (data) {
      args.push(data);
    }

    this.windowInstance._csq.push(...args);
  }

  private logGaEvent(payload: GoogleAnalyticsPayload): void {
    this.tealiumAnalytics.logEvent(payload);
  }

  private logClickstreamEvent(payload: ClickstreamPayload) {
    this.windowInstance._csq.push({
      v2: {
        namespace: 'consumer',
        version: 1,
        name: payload.impressions ? 'moduleVisible' : 'impressionClicked',
        data: payload,
      },
    } as ClickstreamEventsPayload);
  }

  private logClickstreamExperiment(payload: ClickstreamExperimentPayload) {
    this.windowInstance._csq.push({
      v2: {
        namespace: 'clickstream',
        name: 'experimentTreatments',
        version: 1,
        data: {
          provider: payload.provider,
          providerSessionId: payload.providerSessionId
            ? {
                string: payload.providerSessionId,
              }
            : null,
          treatments: [
            {
              experimentId: {
                string: payload.experimentId,
              },
              experimentName: payload.experimentName,
              treatmentId: payload.treatmentId ?? null,
              treatmentName: payload.treatmentName ?? payload.treatment,
              isApplied: true,
            },
          ],
        },
      },
    } as ClickstreamEventsPayload);
  }
}
